import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import * as React from "react";
import { useApi } from "../../containers/ApiContainer";

interface TestFBO {
  fbo_id: string;
  fbo_name: string;
  reason: string;
}

interface AirPlxEmployee {
  email: string;
}

export const ActivityFilters = () => {
  const { postgrest } = useApi();
  const api = postgrest;
  const [testFBOs, setTestFBOs] = React.useState<TestFBO[]>([]);
  const [employees, setEmployees] = React.useState<AirPlxEmployee[]>([]);
  const [allFBOs, setAllFBOs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [addFBODialog, setAddFBODialog] = React.useState(false);
  const [addEmployeeDialog, setAddEmployeeDialog] = React.useState(false);
  const [selectedFBO, setSelectedFBO] = React.useState<any>(null);
  const [reason, setReason] = React.useState("");
  const [email, setEmail] = React.useState("");

  const testFBOColumns: GridColDef[] = [
    {
      field: "fbo_name",
      headerName: "FBO Name",
      width: 300,
      editable: false,
    },
    {
      field: "reason",
      headerName: "Reason",
      width: 400,
      editable: false,
      valueGetter: (_, row) => row.reason || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleRemoveTestFBO(params.row.fbo_id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      // Load test FBOs
      const testFBOsRes = await postgrest
        .from("test_fbos")
        .select("*, fbo:fbo_id(id, name)");
      if (testFBOsRes.data) {
        setTestFBOs(
          testFBOsRes.data.map((tf: any) => ({
            fbo_id: tf.fbo_id,
            fbo_name: tf.fbo?.name || "Unknown",
            reason: tf.reason,
          }))
        );
      }

      // Load AirPlx employees
      const employeesRes = await postgrest
        .from("airplx_employees")
        .select("email");
      if (employeesRes.data) {
        setEmployees(employeesRes.data);
      }

      // Load all FBOs for autocomplete
      const fbosRes = await postgrest
        .from("fbo")
        .select("id, name")
        .order("name");
      if (fbosRes.data) {
        setAllFBOs(fbosRes.data);
      }
    } catch (err) {
      console.error("Error loading filter data:", err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const handleAddTestFBO = async () => {
    if (!selectedFBO) return;

    try {
      const res = await api.rpc("api_manage_test_fbo", {
        p_fbo_id: selectedFBO.id,
        p_is_test: true,
        p_reason: reason || null,
      });

      if (res.data?.success) {
        await loadData();
        setAddFBODialog(false);
        setSelectedFBO(null);
        setReason("");
      }
    } catch (err) {
      console.error("Error adding test FBO:", err);
    }
  };

  const handleRemoveTestFBO = async (fbo_id: string) => {
    try {
      const res = await api.rpc("api_manage_test_fbo", {
        p_fbo_id: fbo_id,
        p_is_test: false,
      });

      if (res.data?.success) {
        await loadData();
      }
    } catch (err) {
      console.error("Error removing test FBO:", err);
    }
  };

  const handleAddEmployee = async () => {
    if (!email) return;

    try {
      const res = await api.rpc("api_manage_airplx_employee", {
        p_email: email,
        p_is_employee: true,
      });

      if (res.data?.success) {
        await loadData();
        setAddEmployeeDialog(false);
        setEmail("");
      }
    } catch (err) {
      console.error("Error adding employee:", err);
    }
  };

  const handleRemoveEmployee = async (email: string) => {
    try {
      const res = await api.rpc("api_manage_airplx_employee", {
        p_email: email,
        p_is_employee: false,
      });

      if (res.data?.success) {
        await loadData();
      }
    } catch (err) {
      console.error("Error removing employee:", err);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: 800, mx: "auto", p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6">Activity Tracking Filters</Typography>
        <Tooltip title="These filters exclude FBOs and users from activity reports and metrics.">
          <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
        </Tooltip>
      </Stack>

      {/* Test/Demo FBOs */}
      <Stack
        direction="column"
        spacing={2}
        sx={{ width: "100%", minWidth: 700, p: 1 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>Test/Demo FBOs</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddFBODialog(true)}
          >
            Add Test FBO
          </Button>
        </Box>

        <div style={{ height: 300, width: "100%" }}>
          <DataGrid
            rows={testFBOs.map((fbo) => ({ ...fbo, id: fbo.fbo_id }))}
            columns={testFBOColumns}
            pageSizeOptions={[20]}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            getRowId={(row) => row.fbo_id}
          />
        </div>
      </Stack>

      {/* AirPlx Employees */}
      <Stack
        direction="column"
        spacing={2}
        sx={{ width: "100%", minWidth: 700, p: 1 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>AirPlx Employees</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddEmployeeDialog(true)}
          >
            Add Employee
          </Button>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={1}>
          <Chip
            label="*@airplx.com (auto-excluded)"
            color="primary"
            variant="outlined"
          />
            {employees.map((emp) => (
              <Chip
                key={emp.email}
                label={emp.email}
                onDelete={() => handleRemoveEmployee(emp.email)}
              />
            ))}
            {employees.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                No additional employees configured (all @airplx.com emails are
                automatically excluded)
              </Typography>
            )}
          </Box>
        </Stack>

      {/* Add Test FBO Dialog */}
      <Dialog
        open={addFBODialog}
        onClose={() => setAddFBODialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Test/Demo FBO</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={allFBOs}
              getOptionLabel={(option) => option.name}
              value={selectedFBO}
              onChange={(_, value) => setSelectedFBO(value)}
              renderInput={(params) => (
                <TextField {...params} label="Select FBO" required />
              )}
            />
            <TextField
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Internal testing account"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFBODialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddTestFBO}
            variant="contained"
            disabled={!selectedFBO}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog
        open={addEmployeeDialog}
        onClose={() => setAddEmployeeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add AirPlx Employee</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            sx={{ mt: 1 }}
            type="email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEmployeeDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddEmployee}
            variant="contained"
            disabled={!email}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
